import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, UserWithRole } from "@/hooks/auth";
import { authCookies } from "@/lib/cookieUtils";
import supabase from "@/lib/supabase";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "admin" | "user";
}

export const ProtectedRoute = ({
  children,
  requiredRole,
}: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [isContentReady, setIsContentReady] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have auth token in cookies
      const authToken = authCookies.getAuthToken();
      
      if (authToken && !user && !isLoading) {
        // We have a token but no user - try to restore session
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            // Session exists, wait for auth context to update
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error("Error restoring session:", error);
        }
      }
      
      setIsCheckingAuth(false);
    };
    
    checkAuth();
  }, []);
  
  useEffect(() => {
    // تسجيل رسائل للتصحيح
    console.log("ProtectedRoute - حالة المصادقة:", {
      isLoading,
      isAuthenticated: !!user,
      path: location.pathname,
      requiredRole,
      userRole: user?.role,
      isCheckingAuth,
    });

    // للتأكد من عدم استمرار حالة التحميل لفترة طويلة
    const maxLoadingTimeout = setTimeout(() => {
      if (isLoading) {
        console.log(
          "ProtectedRoute - تجاوز وقت التحميل الأقصى، إظهار المحتوى على أي حال"
        );
        setIsContentReady(true);
      }
    }, 3000); // 3 ثواني كحد أقصى للانتظار

    // إذا تم المصادقة وانتهى التحميل، نعيّن الحالة إلى جاهز فورًا
    if (!isLoading && user && !isContentReady) {
      console.log("جاري تحميل مكونات الصفحة...");
      setIsContentReady(true);
      console.log("تم الانتهاء من تحميل المكونات");
    }

    // إذا لم يكن هناك مستخدم ولكن الكوكيز تشير إلى تسجيل دخول، فربما تكون الجلسة قيد التحميل
    const isLoggedIn = authCookies.getAuthState();
    if (!user && isLoggedIn && isLoading) {
      console.log(
        "ProtectedRoute - المستخدم يبدو مسجل الدخول في الكوكيز لكن ليس في حالة المستخدم"
      );
      // سنضع مؤقتًا للتأكد من عدم استمرار حالة التحميل لفترة طويلة
      // نشير إلى أن المحتوى جاهز لتجنب التعليق على حالة التحميل
      setIsContentReady(true);
    }

    // تنظيف المؤقتات عند إلغاء تركيب المكون
    return () => {
      clearTimeout(maxLoadingTimeout);
    };
  }, [isLoading, user, location.pathname, requiredRole, isCheckingAuth]); // removed isContentReady to avoid re-render loops

  // أثناء التحميل أو التحقق من المصادقة
  if ((isLoading || isCheckingAuth) && !isContentReady) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-700 text-lg font-medium">
          جاري تحميل المحتوى...
        </p>
        <p className="text-gray-500 text-sm mt-2">يرجى الانتظار</p>
      </div>
    );
  }

  // إذا لم يكن المستخدم مسجل الدخول، نوجهه إلى صفحة تسجيل الدخول
  if (!user && !isLoading && !isCheckingAuth) {
    // تحقق من حالة تسجيل الدخول في الكوكيز
    const isLoggedIn = authCookies.getAuthState();
    const authToken = authCookies.getAuthToken();
    
    console.log("ProtectedRoute - حالة تسجيل الدخول (cookies):", {
      isLoggedIn,
      hasAuthToken: !!authToken,
      path: location.pathname
    });

    // إذا كان هناك توكن في الكوكيز، انتظر قليلاً لإعطاء فرصة للمصادقة للتحميل
    if (authToken) {
      console.log(
        "ProtectedRoute - يوجد توكن في الكوكيز، عرض شاشة التحميل مؤقتاً"
      );

      // عرض شاشة التحميل مع انتظار قصير
      return (
        <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">
            جاري استعادة الجلسة...
          </p>
          <p className="text-gray-500 text-sm mt-2">يرجى الانتظار</p>
        </div>
      );
    }

    // إذا لم يكن هناك توكن في الكوكيز، نوجهه إلى صفحة تسجيل الدخول
    console.log("ProtectedRoute - لا يوجد توكن، إعادة توجيه إلى صفحة تسجيل الدخول");
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // التحقق من دور المستخدم إذا كان هناك دور مطلوب للوصول
  if (requiredRole && user) {
    // الحصول على دور المستخدم، بما في ذلك التعامل مع المستخدمين القدامى
    const userRole = (user as UserWithRole).role || "user";

    console.log(
      `ProtectedRoute - فحص دور المستخدم. المطلوب: ${requiredRole}, الحالي: ${userRole}, المسار: ${location.pathname}`
    );

    console.log("معلومات المستخدم:", user);
    console.log("رابط الصفحة الحالية:", location.pathname);

    // إذا كان المستخدم العادي يحاول الوصول إلى صفحات الإدارة
    if (requiredRole === "admin" && userRole !== "admin") {
      console.log("المستخدم ليس مسؤولاً، إعادة التوجيه إلى الصفحة الرئيسية");

      // تسجيل محاولة وصول غير مسموح بها
      console.warn(
        `محاولة وصول غير مصرح: المستخدم ${user.email} (${userRole}) يحاول الوصول إلى صفحة إدارية: ${location.pathname}`
      );

      // التحقق من وجود عنوان بريد إلكتروني للمستخدم
      if (!user.email) {
        console.warn(
          "المستخدم ليس لديه بريد إلكتروني، قد يكون هذا مستخدم افتراضي أو خطأ مصادقة"
        );
      }

      return <Navigate to="/" state={{ from: location }} replace />;
    }

    // إذا كان المسؤول يحاول الوصول إلى صفحات المستخدم العادي
    if (requiredRole === "user" && userRole === "admin") {
      console.log(
        "المستخدم مسؤول ويحاول الوصول إلى صفحة المستخدم، إعادة التوجيه إلى لوحة ويدجت المسؤول"
      );
      return <Navigate to="/admin/home" state={{ from: location }} replace />;
    }
  }

  // إذا كان المستخدم مصادقًا وله الدور المناسب، نعرض المحتوى المحمي
  return <>{children}</>;
};
